import React from 'react';
import { useFormik } from 'formik';
import moment from 'moment';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';

import SelectField from 'components/SelectField';
import AppContext from 'contexts/AppContext';
import UserRoleType from 'types/enums/UserRoleType';
import Venue from 'types/models/Venue';
import Commerce from 'types/models/Commerce';
import { getVenuesWithStockModuleActive } from 'services/venuesServices';
import { getCommercesByVenue } from 'services/commerceServices';
import {
  exportStockProfitability,
  getStockProfitability,
  getStockProfitabilitySummary,
} from 'services/stockProfitabilityServices';
import {
  StockProfitabilityFilters,
  StockProfitabilityResponse,
  StockProfitabilityRow,
  StockProfitabilitySummary,
} from 'types/models/StockProfitability';
import { StockProfitabilityForm } from 'forms/schemas/StockProfitabilityForm';
import { parseDateToFormik, parseDateTimeLumen } from 'utils/datetime';
import { formatMoneyAuto } from 'utils/formatMoney';
import useSetupInitialState from 'hooks/useSetupInitialState';
import { getApiErrorMessage } from 'utils/errors';
import SaveError from 'components/common/SaveError';
import { mapToSelectOptions } from 'utils/select';
import 'styles/pages/StockProfitabilityPage.scss';

const formatPercent = (value: number | null): string => (value === null || isNaN(value) ? '—' : `${value.toFixed(0)}%`);

const formatMoneyOrDash = (value: number | null): string =>
  value === null || isNaN(value) ? '—' : formatMoneyAuto(value);

const ProfitabilityReportForm = ({
  onSearch,
  onExport,
  isLoading,
  isExporting,
}: {
  onSearch: (filters: StockProfitabilityFilters) => void;
  onExport: (filters: StockProfitabilityFilters) => void;
  isLoading: boolean;
  isExporting: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const { authData } = React.useContext(AppContext);
  const isAdmin = authData?.user?.role === UserRoleType.Admin;
  const isVenueEditor = authData?.user?.role === UserRoleType.VenueEditor;

  const entity = useSetupInitialState();

  const [venues, setVenues] = React.useState<Venue[] | null>(null);
  const [venueCommerces, setVenueCommerces] = React.useState<Commerce[]>([]);
  const [loadingFilters, setLoadingFilters] = React.useState(false);

  const [defaultRange] = React.useState(() => ({
    from: moment().subtract(7, 'days').toDate(),
    to: moment().add(1, 'day').endOf('day').toDate(),
  }));

  const formik = useFormik<StockProfitabilityFilters>({
    enableReinitialize: true,
    initialValues: {
      venueId: entity?.id ?? '',
      commerceIds: [],
      dateFrom: parseDateToFormik(defaultRange.from, ''),
      dateTo: parseDateToFormik(defaultRange.to, ''),
      includeWarehouseLosses: true,
    },
    validationSchema: StockProfitabilityForm(t),
    onSubmit: () => runReport(),
  });

  const getEffectiveFilters = (): StockProfitabilityFilters => ({
    venueId: formik.values.venueId,
    commerceIds:
      formik.values.commerceIds.length > 0 ? formik.values.commerceIds : venueCommerces.map((commerce) => commerce.id),
    dateFrom: parseDateTimeLumen(moment(formik.values.dateFrom).toDate(), '') ?? '',
    dateTo: parseDateTimeLumen(moment(formik.values.dateTo).toDate(), '') ?? '',
    includeWarehouseLosses: formik.values.includeWarehouseLosses,
  });

  const runReport = () => {
    if (!formik.values.venueId || loadingFilters) return;
    onSearch(getEffectiveFilters());
  };

  React.useEffect(() => {
    if (!isAdmin) return;
    let mounted = true;
    setLoadingFilters(true);
    const loadVenues = async () => {
      try {
        const response = await getVenuesWithStockModuleActive();
        if (mounted) setVenues(response?.data ?? []);
      } finally {
        if (mounted) setLoadingFilters(false);
      }
    };
    loadVenues();
    return () => {
      mounted = false;
    };
  }, [isAdmin]);

  React.useEffect(() => {
    if (!formik.values.venueId) {
      setVenueCommerces([]);
      return;
    }
    const selectedVenue = isVenueEditor
      ? (entity as Venue)
      : venues?.find((venue) => venue.id === formik.values.venueId);
    if (!selectedVenue) return;

    let mounted = true;
    setLoadingFilters(true);
    const handleVenueCommerces = async () => {
      try {
        const allCommerces = await getCommercesByVenue({ venueId: selectedVenue.id });
        if (mounted) setVenueCommerces(allCommerces ?? []);
      } finally {
        if (mounted) setLoadingFilters(false);
      }
    };
    handleVenueCommerces();

    if (formik.values.commerceIds.length > 0) {
      formik.setFieldValue('commerceIds', []);
    }
    return () => {
      mounted = false;
    };
  }, [formik.values.venueId, venues, entity, isVenueEditor]);

  return (
    <>
      <div className="Grid--row">
        {authData?.user?.role === UserRoleType.Admin && (
          <SelectField
            label={t('stock.profitability.location-venue')}
            name="venueId"
            placeholder={t('stock.profitability.location-venue-placeholder')}
            value={formik.values.venueId}
            options={mapToSelectOptions(venues ?? [], { label: 'name' })}
            isMulti={false}
            setFieldValue={formik.setFieldValue}
            onBlur={formik.handleBlur}
            touched={formik.touched.venueId}
            error={formik.errors.venueId}
            isClearable={false}
            backspaceRemovesValue={false}
            isDisabled={isLoading}
            isSearchable
          />
        )}
        <SelectField
          label={t('stock.profitability.location')}
          name="commerceIds"
          placeholder={t('stock.profitability.location-placeholder')}
          value={formik.values.commerceIds}
          options={mapToSelectOptions(venueCommerces ?? [], { label: 'name' })}
          isMulti
          setFieldValue={formik.setFieldValue}
          onBlur={formik.handleBlur}
          touched={formik.touched.commerceIds}
          error={formik.errors.commerceIds}
          isClearable={false}
          backspaceRemovesValue={false}
          isDisabled={isLoading || !formik.values.venueId}
          isSearchable
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formik.values.includeWarehouseLosses}
              onChange={(e) => formik.setFieldValue('includeWarehouseLosses', e.target.checked)}
              color="primary"
            />
          }
          label={t('stock.profitability.include-warehouse-losses')}
        />
      </div>

      <div style={{ display: 'flex', gap: '1rem' }}>
        <TextField
          margin="dense"
          fullWidth
          name="dateFrom"
          label={t('stock.profitability.dateFrom')}
          value={formik.values.dateFrom}
          onChange={formik.handleChange}
          disabled={loadingFilters || (isAdmin && !formik.values.venueId)}
          type="datetime-local"
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          margin="dense"
          fullWidth
          name="dateTo"
          label={t('stock.profitability.dateTo')}
          value={formik.values.dateTo}
          onChange={formik.handleChange}
          disabled={loadingFilters || (isAdmin && !formik.values.venueId)}
          type="datetime-local"
          InputLabelProps={{ shrink: true }}
        />
      </div>

      <Button
        className="btn-50 my-6"
        variant="contained"
        color="primary"
        type="submit"
        disabled={!formik.values.venueId || loadingFilters || isLoading}
        onClick={runReport}
      >
        {t('search-label')}
      </Button>
      <Button
        className="btn-50 my-6"
        variant="outlined"
        color="primary"
        type="button"
        disabled={!formik.values.venueId || loadingFilters || isLoading || isExporting}
        onClick={() => onExport(getEffectiveFilters())}
      >
        {isExporting ? t('loading') : t('stock.profitability.export')}
      </Button>
    </>
  );
};

export default (): JSX.Element => {
  const { t } = useTranslation();

  const [filters, setFilters] = React.useState<StockProfitabilityFilters | null>(null);
  const [response, setResponse] = React.useState<StockProfitabilityResponse | null>(null);
  const [, setSummary] = React.useState<StockProfitabilitySummary | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isExporting, setIsExporting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [expandedId, setExpandedId] = React.useState<string | null>(null);

  const loadData = React.useCallback(
    async (searchFilters: StockProfitabilityFilters, page: number) => {
      setIsLoading(true);
      try {
        const [result, summaryResult] = await Promise.all([
          getStockProfitability(searchFilters, page),
          getStockProfitabilitySummary(searchFilters),
        ]);
        setResponse(result);
        setSummary(summaryResult);
      } catch (error) {
        console.error(error);
        setError(getApiErrorMessage(error) ?? t('errors.generic'));
      } finally {
        setIsLoading(false);
      }
    },
    [t],
  );

  React.useEffect(() => {
    if (filters) {
      loadData(filters, 1);
    }
  }, [filters, loadData]);

  const handlePageChange = (page: number) => {
    if (!filters) return;
    loadData(filters, page);
  };

  const handleExport = async (exportFilters: StockProfitabilityFilters) => {
    if (!exportFilters.venueId || isExporting) return;

    setIsExporting(true);
    try {
      const blob = await exportStockProfitability(exportFilters);
      if (!blob) {
        console.error('Export failed');
        return;
      }

      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(
        new Blob([blob], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
      );
      a.download = 'rentabilidad_stock.xlsx';
      a.click();
      window.URL.revokeObjectURL(a.href);
    } catch (error) {
      console.error(error);
    } finally {
      setIsExporting(false);
    }
  };

  const hasSearched = filters !== null;

  return (
    <>
      <ProfitabilityReportForm
        onSearch={setFilters}
        onExport={handleExport}
        isLoading={isLoading}
        isExporting={isExporting}
      />

      {isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
          <CircularProgress />
        </div>
      )}
      <SaveError error={error} />
      {hasSearched && !isLoading && response && (
        <>
          <TableContainer component={Paper} className="table-container" style={{ marginTop: 20 }}>
            <Table>
              <TableHead className="table-header">
                <TableRow>
                  <TableCell />
                  <TableCell>{t('stock.profitability.col-product')}</TableCell>
                  <TableCell align="center">{t('stock.profitability.col-orders')}</TableCell>
                  <TableCell align="center">{t('stock.profitability.col-sales')}</TableCell>
                  <TableCell align="center">{t('stock.profitability.col-avg-cost')}</TableCell>
                  <TableCell align="center">{t('stock.profitability.col-sale-price')}</TableCell>
                  <TableCell align="center">{t('stock.profitability.col-profit-amount')}</TableCell>
                  <TableCell align="center">{t('stock.profitability.col-profit-percent')}</TableCell>
                  <TableCell align="center">{t('stock.profitability.col-real-amount')}</TableCell>
                  <TableCell align="center">{t('stock.profitability.col-real-percent')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {response.products.length > 0 ? (
                  <>
                    {response.products.map((row: StockProfitabilityRow) => {
                      const isExpanded = expandedId === row.globalProductId;
                      return (
                        <React.Fragment key={row.globalProductId}>
                          <TableRow hover>
                            <TableCell>
                              <IconButton
                                size="small"
                                aria-label={t('stock.profitability.toggle-detail')}
                                onClick={() => setExpandedId(isExpanded ? null : row.globalProductId)}
                              >
                                {isExpanded ? <ExpandLess /> : <ExpandMore />}
                              </IconButton>
                            </TableCell>
                            <TableCell component="th" scope="row">
                              {row.name}
                            </TableCell>
                            <TableCell align="center">{row.orders}</TableCell>
                            <TableCell align="center">{row.salesUnits}</TableCell>
                            <TableCell align="center">{formatMoneyAuto(row.averageCost)}</TableCell>
                            <TableCell align="center">{formatMoneyAuto(row.salePrice)}</TableCell>
                            <TableCell align="center">{formatMoneyOrDash(row.profitabilityAmount)}</TableCell>
                            <TableCell align="center">{formatPercent(row.profitabilityPercent)}</TableCell>
                            <TableCell align="center">{formatMoneyOrDash(row.realProfitabilityAmount)}</TableCell>
                            <TableCell align="center">{formatPercent(row.realProfitabilityPercent)}</TableCell>
                          </TableRow>
                          {isExpanded && (
                            <TableRow>
                              <TableCell colSpan={10} className="profitability-detail-cell">
                                <div className="profitability-detail">
                                  <div className="profitability-detail__item">
                                    <span>{t('stock.profitability.detail-replacement-cost')}</span>
                                    <strong>{formatMoneyAuto(row.replacementCost)}</strong>
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                    <TableRow className="total-row">
                      <TableCell />
                      <TableCell component="th" scope="row">
                        <strong>{t('stock.profitability.total')}</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>{response.totals.orders}</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>{response.totals.salesUnits}</strong>
                      </TableCell>
                      <TableCell align="center">—</TableCell>
                      <TableCell align="center">—</TableCell>
                      <TableCell align="center">
                        <strong>{formatMoneyOrDash(response.totals.profitabilityAmount)}</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>{formatPercent(response.totals.profitabilityPercent)}</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>{formatMoneyOrDash(response.totals.realProfitabilityAmount)}</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>{formatPercent(response.totals.realProfitabilityPercent)}</strong>
                      </TableCell>
                    </TableRow>
                  </>
                ) : (
                  <TableRow>
                    <TableCell colSpan={10}>{t('stock.profitability.no-results')}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {response.pagination && response.pagination.total > 0 && (
            <TablePagination
              component="div"
              count={response.pagination.total}
              page={response.pagination.currentPage - 1}
              onPageChange={(_, page: number) => handlePageChange(page + 1)}
              rowsPerPage={response.pagination.perPage}
              rowsPerPageOptions={[]}
              labelDisplayedRows={({ from, to, count }) => {
                return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
              }}
            />
          )}

          <Box className="profitability-explain">
            <Typography variant="body2" component="div" className="profitability-explain__body">
              {t('stock.profitability.how-calculated')}
            </Typography>
            <Typography variant="body2" component="div" className="profitability-explain__example">
              {t('stock.profitability.how-calculated-example')}
            </Typography>
            <Typography variant="body2" component="div" className="profitability-explain__example">
              {t('stock.profitability.how-calculated-example-cost')}
            </Typography>
            <Typography variant="body2" component="div" className="profitability-explain__example">
              {t('stock.profitability.how-calculated-real')}
            </Typography>
          </Box>
        </>
      )}
    </>
  );
};
